import React, { useEffect, useMemo, useState } from "react";
import { NutritionDiningStationData } from "../providers/ConfigProvider";
import {
  LayoutRectangle,
  StyleSheet,
  Text,
  View,
  Image,
  StyleProp,
  ViewStyle,
  ActivityIndicator,
} from "react-native";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import { BorderedContainer } from "./BorderedContainer";
import { max, min } from "lodash";
import { config } from "../config";
import { PageBullets } from "./PageBullets";
import { ErrorScreen } from "../screens/ErrorScreen";
import { useTranslation } from "react-i18next";
import noImageSrc from "../../assets/no-image.png";
import { Spacer } from "./ViewComponents";

const baseMinItemHeight = 112; // fixed height
const baseItemWidth = 380; // min column width
const baseImageWidth = 118;
const baseImageHeight = 72;
const minColumns = 1;
const maxColumns = 3;

const makeStyles = (theme: ThemeOptions) =>
  StyleSheet.create({
    header: { alignItems: "center", paddingVertical: theme.spacing.large },
    headerText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.large,
      maxWidth: "80%",
    },
    mainContent: {
      margin: theme.spacing.medium,
    },
    menuItem: {
      minHeight: theme.scaled(baseMinItemHeight),
      justifyContent: "flex-start",
      borderColor: theme.toRgba(theme.contrastColor, 0.2),
      borderBottomWidth: 1,
      padding: theme.spacing.medium,
      overflow: "hidden",
    },
    menuItemText: {
      color: theme.backgroundText,
      fontWeight: "bold",
      fontSize: theme.fontSize.body,
    },
    menuItemDescText: {
      color: theme.backgroundText,
      marginTop: theme.spacing.xsmall,
      fontSize: theme.fontSize.body,
    },
    macros: { flexDirection: "row" },
    macrosText: { color: theme.backgroundText, fontSize: theme.fontSize.small },
    macrosTextNote: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.detail,
    },
    preparingOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      justifyContent: "center",
      alignItems: "center",
    },
  });

export const NutritionDiningStation = ({
  onLoopComplete,
  data,
  menuItemHeight,
  onItemHeightChange,
  style,
}: {
  onLoopComplete: () => void;
  data: NutritionDiningStationData | null;
  // if provided, menuItemHeight will dictate the item height,
  // in order to ensure all stations have the same height
  menuItemHeight?: number;
  onItemHeightChange?: (height: number, forced?: boolean) => void;
  style?: StyleProp<ViewStyle>;
}) => {
  const theme = useTheme();
  const styles = makeStyles(theme);

  const minItemHeight = theme.scaled(baseMinItemHeight);
  const itemWidth = theme.scaled(baseItemWidth);
  const imageWidth = theme.scaled(baseImageWidth);
  const imageHeight = theme.scaled(baseImageHeight);

  const { t } = useTranslation("NutritionDiningStation");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dimensions, setDimensions] = useState<LayoutRectangle>();
  const [stationItemHeight, setStationItemHeight] = useState(minItemHeight);
  const [reducedNumberColumns, setReducedNumberColumns] = useState(false);
  const [preparing, setPreparing] = useState(true);

  const itemHeight = menuItemHeight ?? stationItemHeight;

  // when the item height gets too large, reduce the number of columns used
  useEffect(() => {
    if (reducedNumberColumns) return;
    if (itemHeight > minItemHeight * 1.7) {
      setReducedNumberColumns(true);
      setStationItemHeight(minItemHeight);
      onItemHeightChange?.(minItemHeight, true);
    }
  }, [itemHeight, reducedNumberColumns, onItemHeightChange, minItemHeight]);

  const pages = useMemo(() => {
    if (dimensions == null || data == null) return [];

    const menuItems = data.diningStation.menuItemAppearances
      .sort((i) => i.position)
      .map((i) => i.menuItem);

    let numberColumns = max([
      minColumns,
      min([maxColumns, Math.floor(dimensions.width / itemWidth)]) as number,
    ]) as number;

    if (reducedNumberColumns) {
      numberColumns = Math.max(1, numberColumns - 1);
    }

    const perColumn = max([
      1,
      Math.floor((dimensions.height - theme.spacing.large) / itemHeight),
    ]) as number;

    const numberPages = Math.ceil(
      menuItems.length / (numberColumns * perColumn),
    );

    return Array.from({ length: numberPages }, (_, pageIndex) => {
      const perPage = numberColumns * perColumn;
      const start = pageIndex * perPage;
      const end = start + perPage;
      const pageItems = menuItems.slice(start, end);
      const columns: (typeof menuItems)[] = Array.from(
        { length: numberColumns },
        () => [],
      );

      let columnIndex = 0;
      pageItems.forEach((item) => {
        columns[columnIndex].push(item);
        columnIndex += 1;
        if (columnIndex >= numberColumns) columnIndex = 0;
      });

      return columns.map((col) => {
        const emptyItems = Array.from(
          { length: perColumn - col.length },
          () => null,
        );
        return [...col, ...emptyItems];
      });
    });
  }, [
    dimensions,
    data,
    itemWidth,
    reducedNumberColumns,
    theme.spacing.large,
    itemHeight,
  ]);

  // wait for item height to stop changing before showing the content
  // to prevent the items from flickering as they resize
  useEffect(() => {
    setPreparing(true);
    const timeout = setTimeout(() => setPreparing(false), 1000);
    return () => clearTimeout(timeout);
  }, [itemHeight]);

  useEffect(() => {
    if (currentIndex >= pages.length) {
      setCurrentIndex(0);
    }

    const timeout = setTimeout(() => {
      const next = currentIndex + 1 >= pages.length ? 0 : currentIndex + 1;
      if (next === 0) onLoopComplete();
      setCurrentIndex(next);
    }, config.nutrition.changePageRate);
    return () => clearTimeout(timeout);
  }, [onLoopComplete, pages, currentIndex]);

  const currentPage = pages[currentIndex];

  const columnWidth = useMemo(() => {
    if (dimensions == null || currentPage == null) return 0;

    const gapWidth = theme.spacing.medium * (currentPage.length + 1);
    return (dimensions.width - gapWidth) / currentPage.length;
  }, [dimensions, currentPage, theme.spacing.medium]);

  const textWidth =
    columnWidth -
    theme.spacing.medium -
    (data?.includeImages ? imageWidth + theme.spacing.medium : 0);

  if (data == null) {
    return <ErrorScreen message={t("Failed to fetch dining station")} />;
  }

  return (
    <>
      <BorderedContainer
        style={[styles.mainContent, { width: dimensions?.width }, style]}
        variant="secondary"
      >
        {preparing && (
          <View style={styles.preparingOverlay}>
            <ActivityIndicator size="large" color={theme.backgroundText} />
          </View>
        )}
        <View style={styles.header}>
          <Text style={styles.headerText} numberOfLines={1}>
            {dimensions == null ? " " : data.diningStation.name}
          </Text>
        </View>
        {dimensions == null ? (
          <View
            style={{ flexGrow: 1 }}
            onLayout={(ev) => setDimensions(ev.nativeEvent.layout)}
          />
        ) : (
          currentPage != null && (
            <>
              <View
                style={{ flexDirection: "row", opacity: preparing ? 0 : 1 }}
              >
                {currentPage.map((col, colIndex) => (
                  <View
                    key={`col${colIndex}`}
                    style={{ flexGrow: 1, width: columnWidth }}
                  >
                    {col.map((menuItem, itemIndex) => (
                      <View
                        key={`col${colIndex}row${itemIndex}`}
                        style={[styles.menuItem, { minHeight: itemHeight }]}
                        onLayout={(ev) => {
                          const currentItemHeight =
                            ev.nativeEvent.layout.height;
                          if (currentItemHeight > itemHeight) {
                            setStationItemHeight((prev) =>
                              Math.max(prev, currentItemHeight),
                            );
                            onItemHeightChange?.(currentItemHeight);
                          }
                        }}
                      >
                        {menuItem != null && (
                          <View
                            style={{
                              flexDirection: "row",
                              marginBottom: theme.spacing.small,
                            }}
                          >
                            {data.includeImages && (
                              <Image
                                source={
                                  menuItem.imageUrl != null
                                    ? { uri: menuItem.imageUrl }
                                    : noImageSrc
                                }
                                resizeMode="cover"
                                style={{
                                  width: imageWidth,
                                  height: imageHeight,
                                  marginRight: theme.spacing.medium,
                                }}
                              />
                            )}
                            <View>
                              <View>
                                <Text
                                  style={[
                                    styles.menuItemText,
                                    { width: textWidth },
                                  ]}
                                >
                                  {menuItem.name}
                                </Text>
                                {data.includeDescriptions &&
                                  menuItem.description.length > 0 && (
                                    <Text
                                      style={[
                                        styles.menuItemDescText,
                                        { width: textWidth },
                                      ]}
                                    >
                                      {menuItem.description}
                                    </Text>
                                  )}
                              </View>
                              <Spacer size="xsmall" />
                              <View>
                                {data.includeMacros && (
                                  <View style={styles.macros}>
                                    <Text style={styles.macrosText}>
                                      {menuItem.macros.kcal} Kcal
                                    </Text>
                                    <Spacer size="small" />
                                    <Text style={styles.macrosText}>
                                      CHO: {menuItem.macros.cho}g
                                    </Text>
                                    <Spacer size="small" />
                                    <Text style={styles.macrosText}>
                                      PRO: {menuItem.macros.pro}g
                                    </Text>
                                    <Spacer size="small" />
                                    <Text style={styles.macrosText}>
                                      FAT: {menuItem.macros.fat}g
                                    </Text>
                                  </View>
                                )}
                                {data.includeMacros && menuItem.hasAddOn && (
                                  <Spacer size="xsmall" />
                                )}
                                {menuItem.hasAddOn && (
                                  <Text
                                    style={[
                                      styles.macrosTextNote,
                                      { width: textWidth },
                                    ]}
                                  >
                                    *{" "}
                                    {t(
                                      "Add ons will affect nutrition information",
                                    )}
                                  </Text>
                                )}
                              </View>
                            </View>
                          </View>
                        )}
                      </View>
                    ))}
                  </View>
                ))}
              </View>
              <View style={{ flexGrow: 1 }} />
              <PageBullets total={pages.length} currentIndex={currentIndex} />
            </>
          )
        )}
      </BorderedContainer>
    </>
  );
};
